import { GetStaticProps } from "next"
import Image from "next/image"
import { Container, Box, Grid } from "@mui/material"

import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"
import SEO from "components/SEO"
import Link from "components/i18n/Link"

import useTranslation from "hooks/useTranslation"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchBrandCategories, {
	RawBrandCategory,
} from "utils/commerceTools/graphql/queries/fetchBrandCategories"
import getValueFromLocalizedStringWithFallback from "utils/i18n/getValueFromLocalizedStringWithFallback"
import { getCountryNameFromLocale } from "components/BreadCrumbs"
import {
	getLocalizedMetaTitle,
	getLocalizedMetaDescription,
} from "utils/i18n/getLocalizedMetaAttributeValue"
import { isSwitzerlandLocale } from "utils/seo/pages/fallback"
import { getServerTranslations } from "utils/i18n/getServerTranslations"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import { CurrencyCode } from "@secureo/common/typings/Forex"
import getCurrencySymbol from "@secureo/common/utils/forex/getCurrencySymbol"
import { useContext } from "react"
import AppContext from "@secureo/common/context/AppContext/AppContext"

const getMetaTitleFallback = (locale: string, language: string) => {
	switch (language) {
		case "pl":
			return `Markenübersicht | SafeHero ${getCountryNameFromLocale(locale)}`

		case "de":
		default:
			return `Markenübersicht | SafeHero ${getCountryNameFromLocale(locale)}`
	}
}

const getMetaDescriptionFallback = (
	locale: string,
	language: string,
	currencyCode: CurrencyCode,
) => {
	switch (language) {
		case "pl":
			return `Przegląd produktów marki - zamów teraz bezpośrednio online na SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Wysyłka gratis od ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Zakup na rachunek"
			}`
		case "de":
		default:
			return `Markenübersicht Produkte - jetzt direkt online bestellen auf SafeHero ${getCountryNameFromLocale(
				locale,
			)}! ✓ Portofrei ab ${
				flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents / 100
			}${getCurrencySymbol(currencyCode)} ✓ Trusted Shop®${
				isSwitzerlandLocale(locale) ? "" : " ✓ Kauf auf Rechnung"
			}`
	}
}

const getBrandCategoriesByFirstLetter = (
	sortedBrandCategories: RawBrandCategory[],
	locale: string,
) => {
	const brandCategoriesByFirstLetter: {
		[letter: string]: RawBrandCategory[]
	} = {}

	for (let asciiNumber = 65; asciiNumber <= 90; asciiNumber++) {
		const letter = String.fromCharCode(asciiNumber)

		const brandCategories = sortedBrandCategories.filter((brandCategory) => {
			const name = getValueFromLocalizedStringWithFallback(
				brandCategory.nameAllLocales,
				locale,
			)

			return name.charAt(0) === letter
		})

		if (brandCategories.length > 0) brandCategoriesByFirstLetter[letter] = brandCategories
	}

	return brandCategoriesByFirstLetter
}

const BrandPage = ({ brandOverviewCategory, brandCategories, locale, language }: Props) => {
	const { currencyCode } = useContext(AppContext)
	const brandCategoriesByFirstCharacter = getBrandCategoriesByFirstLetter(brandCategories, locale)
	const { t } = useTranslation("common")

	const breadCrumb: BreadCrumbAncestor = {
		label: t("brands"),
		href: `/brands`,
	}

	const metaTitle =
		getLocalizedMetaTitle(brandOverviewCategory, locale) ||
		getMetaTitleFallback(locale, language)
	const metaDescription =
		getLocalizedMetaDescription(brandOverviewCategory, locale) ||
		getMetaDescriptionFallback(locale, language, currencyCode)

	return (
		<Box my={5}>
			<Box style={{ marginTop: "-40px", paddingBottom: "20px" }}>
				<BreadCrumbs currentPath={breadCrumb} />
			</Box>
			<SEO
				asPath="/brands"
				pageType={"brands"}
				title={metaTitle}
				description={metaDescription}
			/>
			<Box my={5}>
				<Container>
					<Box mb={5} style={{ textAlign: "center" }}>
						<h1>{t("brands")}</h1>
					</Box>

					{Object.entries(brandCategoriesByFirstCharacter).map(
						([letter, brandCategories]) => {
							return (
								<div key={letter}>
									<p style={{ fontSize: 20 }}>
										<strong>{letter}</strong>
									</p>
									<Grid container spacing={3}>
										{brandCategories.map((brandCategory) => {
											const { nameAllLocales, slug, base64svg } =
												brandCategory

											const name = getValueFromLocalizedStringWithFallback(
												nameAllLocales,
												locale,
											)
											return (
												<Grid
													item
													xs={6}
													sm={4}
													md={2}
													key={brandCategory.id}
												>
													<Link href={`/brands/${slug}`}>
														<div
															style={{
																border: "1px solid whitesmoke",
																borderRadius: 3,
																textAlign: "center",
																height: 80,
																display: "flex",
																flexDirection: "row",
																alignItems: "center",
																justifyContent: "center",
																boxShadow:
																	"0px 0px 5px 0px rgba(0,0,0,0.15)",
															}}
														>
															<div
																style={{
																	maxHeight: 60,
																	width: "100px",
																	objectFit: "contain",
																}}
															>
																<Image
																	src={base64svg}
																	alt={name}
																	width={300}
																	height={180}
																	style={{
																		maxHeight: 60,
																		maxWidth: "100%",
																		height: "auto",
																		objectFit: "contain",
																	}}
																/>
															</div>
														</div>
													</Link>
												</Grid>
											)
										})}
									</Grid>
								</div>
							)
						},
					)}
				</Container>
			</Box>
		</Box>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const rawLocale = context.locale
	const { countryCode, language, locale } = normalizeLocale(rawLocale)
	const accessToken = await anonymousAccessTokenCache()
	const { brandOverviewCategory, brandCategories } = await fetchBrandCategories(accessToken)

	const props: Props = {
		brandOverviewCategory,
		brandCategories,
		countryCode,
		language,
		locale,
		...(await getServerTranslations(locale, ["common", "countries", "languages"])),
	}

	return { props }
}

interface Props {
	brandOverviewCategory: RawBrandCategory
	brandCategories: RawBrandCategory[]
	countryCode: CountryCode
	language: string
	locale: string
}

export default BrandPage
